import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, Circle, Loader2 } from "lucide-react";
import { apiPost, friendlyError } from "../../shared/http.js";
import {
  AcceptInviteResponseSchema,
  InvitePreviewResponseSchema,
  PasswordSchema,
  PASSWORD_MIN_LENGTH,
  type InvitePreviewResponse,
} from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "@/ui/form.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card.js";
import { Alert, AlertDescription } from "@/ui/alert.js";

const acceptSchema = z.object({
  displayName: z.string().min(1, "Required").max(80),
  password: PasswordSchema,
});
type AcceptInput = z.infer<typeof acceptSchema>;

interface PasswordRule {
  label: string;
  ok: boolean;
}

function passwordRules(password: string): PasswordRule[] {
  return [
    { label: `At least ${PASSWORD_MIN_LENGTH} characters`, ok: password.length >= PASSWORD_MIN_LENGTH },
    { label: "Lowercase letter", ok: /[a-z]/.test(password) },
    { label: "Uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "Digit", ok: /\d/.test(password) },
    { label: "Symbol", ok: /[^A-Za-z0-9]/.test(password) },
  ];
}

function PasswordChecklist({ password }: { password: string }): JSX.Element {
  return (
    <ul className="space-y-1 text-sm" aria-label="Password requirements">
      {passwordRules(password).map((rule) => (
        <li key={rule.label} className="flex items-center gap-2">
          {rule.ok ? (
            <Check className="size-4 text-primary" aria-hidden />
          ) : (
            <Circle className="size-4 text-muted-foreground" aria-hidden />
          )}
          <span className={rule.ok ? "text-foreground" : "text-muted-foreground"}>
            {rule.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

const INACTIVE_COPY: Record<
  Exclude<InvitePreviewResponse["status"], "active">,
  { title: string; description: string }
> = {
  expired: {
    title: "This invite has expired",
    description: "Ask the household owner to send you a new one.",
  },
  consumed: {
    title: "This invite has already been used",
    description: "If you already created an account, sign in instead.",
  },
  not_found: {
    title: "This invite is no longer active",
    description: "It may have been revoked, expired, or never existed.",
  },
};

export function AcceptInvite(): JSX.Element {
  const { token: pathToken = "" } = useParams<{ token: string }>();
  const [token] = useState<string>(
    () => new URLSearchParams(window.location.search).get("token") ?? pathToken,
  );
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const form = useForm<AcceptInput>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { displayName: "", password: "" },
    // Validate on each keystroke so PASSWORD complexity rules surface as the
    // user types (e.g. "Must contain a digit"), not after submit.
    mode: "onChange",
  });

  // Strip ?token=... from URL on mount (spec §7.2).
  useEffect(() => {
    if (window.location.search.includes("token=")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Pre-flight: ask the server whether this token is still claimable so the user
  // sees status BEFORE filling the form. Pure lookup — doesn't consume.
  useEffect(() => {
    if (token.length !== 64) {
      setPreview({ status: "not_found" });
      return;
    }
    let cancelled = false;
    setPreviewError(null);
    apiPost<unknown>("/invites/preview", { token }, { unauthenticated: true })
      .then((raw) => {
        if (cancelled) return;
        setPreview(InvitePreviewResponseSchema.parse(raw));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreviewError(friendlyError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(values: AcceptInput): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const raw = await apiPost<unknown>(
        "/invites/accept",
        { token, password: values.password, displayName: values.displayName },
        { unauthenticated: true },
      );
      AcceptInviteResponseSchema.parse(raw);
      setDone(true);
      setTimeout(() => void navigate("/login", { replace: true }), 1500);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Welcome to RecipeVault</CardTitle>
            <CardDescription>
              Your account is ready. Redirecting to sign-in…
            </CardDescription>
          </CardHeader>
        </Card>
      </main>
    );
  }

  if (previewError) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Couldn't check your invite</CardTitle>
            <CardDescription>{previewError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!preview) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" aria-hidden />
        <span className="ml-2">Checking your invite…</span>
      </main>
    );
  }

  if (preview.status !== "active") {
    const copy = INACTIVE_COPY[preview.status];
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{copy.title}</CardTitle>
            <CardDescription>{copy.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Join {preview.householdName}</CardTitle>
          <CardDescription>
            You were invited as <span className="text-foreground">{preview.email}</span>.
            Set a display name and password to accept.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        autoFocus
                        maxLength={80}
                        aria-required="true"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>How your name shows up in the household.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <PasswordChecklist password={field.value} />
                  </FormItem>
                )}
              />
              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || token.length !== 64 || !form.formState.isValid}
              >
                {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {submitting ? "Creating account…" : "Accept invite"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}
